import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, InputProps, Modal, Button, InputField } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';
import { toast } from 'utils/toast';

import { closeAndResetModal } from '_common/modals/ModalsSlice';
import { renameObject } from 'App/redux/appSlice';

const MODAL = 'RenameObjectModal';

const RenameObjectModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const isOpen = useSelector((state) => state.modals.open[MODAL]);
  const id = useSelector((state) => state.modals[MODAL].objectId);
  const data = useSelector((state) => state.app.data);
  const submitting = useSelector((state) => state.modals[MODAL].submitting);

  const [name, setName] = useState('');
  const [validations, setValidations] = useState<{ name?: string }>({});

  const getDocumentName = useCallback(() => {
    const { name, type } = data[id];

    if (type === 'file' || type === 'folder') {
      return name;
    }

    const lastDot = name.lastIndexOf('.');
    return lastDot === -1 ? name : name.slice(0, lastDot);
  }, [data, id]);

  useEffect(() => {
    if (isOpen) {
      setName(getDocumentName());
    }
  }, [isOpen, getDocumentName]);

  const onNameChanged: InputProps['onChange'] = (e) => {
    const newValidations: typeof validations = {};
    const inputName = e.target.value;
    setName(inputName);

    if (new RegExp(/[<>/:?*"|\\]/).test(inputName)) {
      newValidations.name = intl.formatMessage(
        {
          id: 'UNSUPPORTED_CHARACTER_MESSAGE',
        },
        { type: data[id].type === 'dopdf' ? 'doPDF' : data[id].type },
      );
    } else if (e.target.value.length === 0) {
      newValidations.name = intl.formatMessage({
        id: 'validation.name.insertName',
      });
    } else if (inputName.length > 150) {
      newValidations.name = intl.formatMessage({ id: 'validation.name.longName' }, { length: 150 });
    } else {
      newValidations.name = '';
    }
    if (Object.keys(newValidations).length > 0) {
      setValidations(newValidations);
    }
  };

  const handleRename = () => {
    dispatch(
      renameObject({
        objectId: id,
        objectType: data[id].type,
        newName: name,
      }),
    ).then(() => {
      toast({
        variant: 'success',
        title: 'FILE_RENAMED',
        message: 'RENAME_FILE_MESSAGE',
        messageValues: { oldName: data[id].name, newName: name },
      });
    });
  };

  const close = () => {
    setValidations({});
    setName('');
    dispatch(closeAndResetModal(MODAL));
  };

  if (!data[id]) {
    return null;
  }

  return (
    <Modal open={!!isOpen} width="75rem" close={close} testId="rename-object">
      <Modal.Header>
        <FormattedMessage id="storage.modals.rename.title" />
      </Modal.Header>

      <Modal.Body>
        <InputField
          label={intl.formatMessage(
            { id: 'EDIT_TITLE' },
            { value: data[id].type === 'dopdf' ? 'doPDF' : data[id].type },
          )}
          size="l"
          feedbackContent={validations.name ? validations.name : undefined}
          testId="rename-object-modal"
        >
          <Input
            value={name}
            onChange={onNameChanged}
            hasError={!!validations.name}
            placeholder={intl.formatMessage(
              { id: 'EDIT_TITLE_PLACEHOLDER' },
              { value: data[id].type },
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && validations.name === '') {
                e.preventDefault();
                handleRename();
              }
            }}
            size="l"
            testId="rename-object-modal"
          />
        </InputField>
      </Modal.Body>

      <Modal.Footer>
        <Button size="m" variant="standard" onClick={close} testId="rename-object-modal-close">
          <FormattedMessage id="global.close" />
        </Button>
        <Button
          size="m"
          variant="primary"
          loading={submitting}
          onClick={handleRename}
          style={{ margin: '0 0 0 1rem' }}
          disabled={!!validations.name || name === ''}
          testId="rename-object-modal-cta"
        >
          <FormattedMessage id="global.rename" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RenameObjectModal;
